import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  format,
  parseISO,
  addDays,
  startOfWeek,
  isSameDay,
  isToday,
  isPast,
} from 'date-fns';
import {
  Calendar,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Phone as PhoneIcon,
  FileText,
  Search,
  Users,
  X,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { bookingService } from '@/services/api/booking.service';
import { adminBookingService } from '@/services/api/admin-booking.service';
import { organizationService } from '@/services/api/organization.service';
import { customerService } from '@/services/api/customer.service';
import type { Service, AvailableSlot, AdminBookingCreate } from '@/types/booking';
import type { Customer } from '@/types/customer';

import { Button } from '@/components/ui/button';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PhoneInput } from '@/components/ui/phone-input';
import { Badge } from '@/components/ui/badge';
import { RadioGroup } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SearchBar } from '@/components/ui/search-bar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper, StepCard, type StepState } from '@/components/booking/Stepper';
import { BookingTypeStep, type BookingType } from '@/components/booking/BookingTypeStep';
import { BookingSummaryCard } from '@/components/booking/BookingSummaryCard';
import { ServiceListRow } from '@/components/booking/ServiceListRow';
import { TimeSlotGrid, type TimeSlot as GridTimeSlot } from '@/components/booking/TimeSlotGrid';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/contexts/auth-context';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';

// Dynamic Zod validation schema for customer information (mirrors public book.tsx pattern)
function createCustomerInfoSchema(requirePhone: boolean) {
  return z.object({
    email: z.string().email('Invalid email format'),
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
    phone: requirePhone
      ? z.string().min(1, 'Phone number is required').refine(
          (val) => isValidPhoneNumber(val),
          'Please enter a valid phone number for the selected country'
        )
      : z.string()
          .optional()
          .or(z.literal(''))
          .refine(
            (val) => !val || val === '' || isValidPhoneNumber(val),
            'Please enter a valid phone number for the selected country'
          ),
    notes: z.string().optional(),
    status: z.enum(['pending', 'confirmed']),
  });
}

type CustomerInfoFormData = z.infer<ReturnType<typeof createCustomerInfoSchema>>;

type WizardStep = 0 | 1 | 2 | 3 | 4;
type CustomerMode = 'existing' | 'new';

const SEARCH_DEBOUNCE_MS = 250;

function isGroupService(svc: Service): boolean {
  const cap = svc.max_capacity ?? svc.maxCapacity ?? 0;
  return cap > 1 || !!(svc.group_booking ?? svc.groupBooking);
}

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

export default function NewBookingPage() {
  const [_, setLocation] = useLocation();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation('bookings');
  const { user } = useAuth();

  useSetPageHeader({ title: t('newBooking.title'), sub: t('newBooking.subtitle') }, [t]);

  const presetCustomerId = useMemo(() => getQueryParam('customer_id'), []);
  const initialStep = useMemo<WizardStep>(() => {
    const raw = getQueryParam('step');
    const parsed = raw ? Number(raw) : NaN;
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 5) {
      return (parsed - 1) as WizardStep;
    }
    return 0;
  }, []);

  // Fetch organization to check require_phone setting
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });
  const requirePhone = organization?.booking_settings?.require_phone ?? false;

  const customerInfoSchema = useMemo(() => createCustomerInfoSchema(requirePhone), [requirePhone]);

  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [bookingType, setBookingType] = useState<BookingType>('one_time');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set());
  const [hasFutureSlots, setHasFutureSlots] = useState<boolean | null>(null);

  // Customer step state
  const [customerMode, setCustomerMode] = useState<CustomerMode>(
    presetCustomerId ? 'existing' : 'existing',
  );
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');

  // React Hook Form for new-customer information
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset: resetForm,
  } = useForm<CustomerInfoFormData>({
    resolver: zodResolver(customerInfoSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      notes: '',
      status: 'confirmed',
    },
  });

  const formData = watch();

  // Preload customer when ?customer_id=… is present
  const { data: presetCustomer } = useQuery({
    queryKey: ['customer', presetCustomerId],
    queryFn: () => customerService.getCustomer(presetCustomerId!),
    enabled: !!presetCustomerId,
  });

  useEffect(() => {
    if (presetCustomer) {
      setSelectedCustomer(presetCustomer);
      setCustomerMode('existing');
    }
  }, [presetCustomer]);

  // Debounce customer search
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedCustomerSearch(customerSearch.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [customerSearch]);

  const { data: customerSearchResult, isFetching: isSearchingCustomers } = useQuery({
    queryKey: ['customer-search', debouncedCustomerSearch],
    queryFn: () =>
      customerService.list({
        search: debouncedCustomerSearch || undefined,
        page: 1,
        perPage: 8,
      }),
    enabled: customerMode === 'existing' && currentStep === 3,
  });

  // Fetch active services
  const { data: services = [], isLoading: isLoadingServices } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });

  const activeServices = useMemo(
    () => services.filter((service) => service.active),
    [services]
  );

  const [slotsData, setSlotsData] = useState<AvailableSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedService || currentStep !== 2 || !user?.organizationId) return;
      setIsLoadingSlots(true);
      try {
        const slots = await adminBookingService.getAvailabilitySlotsForDate({
          serviceId: selectedService.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          organizationId: user.organizationId,
        });
        setSlotsData(slots);
      } catch (error) {
        console.error('Failed to load slots:', error);
        setSlotsData([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    loadSlots();
  }, [selectedService, selectedDate, currentStep, user?.organizationId]);

  useEffect(() => {
    const loadWeekDates = async () => {
      if (!selectedService || currentStep !== 2 || !user?.organizationId) return;
      try {
        const weekEnd = addDays(currentWeekStart, 6);
        const availableDates = await adminBookingService.getAvailableDatesInRange({
          serviceId: selectedService.id,
          startDate: format(currentWeekStart, 'yyyy-MM-dd'),
          endDate: format(weekEnd, 'yyyy-MM-dd'),
          organizationId: user.organizationId,
        });
        setDatesWithSlots(availableDates);
      } catch (error) {
        console.error('Failed to load week dates:', error);
        setDatesWithSlots(new Set());
      }
    };

    loadWeekDates();
  }, [selectedService, currentWeekStart, currentStep, user?.organizationId]);

  // 30-day lookahead: detect when service has zero future availability so we can
  // show a clear "no slots" message instead of letting users page forever.
  useEffect(() => {
    if (!selectedService || !user?.organizationId) {
      setHasFutureSlots(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const today = new Date();
        const endDate = addDays(today, 30);
        const dates = await adminBookingService.getAvailableDatesInRange({
          serviceId: selectedService.id,
          startDate: format(today, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          organizationId: user.organizationId,
        });
        if (!cancelled) setHasFutureSlots(dates.size > 0);
      } catch (error) {
        console.error('Failed to check future availability:', error);
        if (!cancelled) setHasFutureSlots(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedService, user?.organizationId]);

  // Drop today's dot if no future slots remain (backend doesn't filter by time-of-day).
  useEffect(() => {
    if (!isToday(selectedDate)) return;
    const todayKey = format(selectedDate, 'yyyy-MM-dd');
    const now = Date.now();
    const hasFuture = slotsData.some((slot) => {
      const startStr = slot.startTime || slot.start_time;
      if (!startStr) return false;
      return parseISO(startStr).getTime() > now;
    });
    setDatesWithSlots((prev) => {
      const has = prev.has(todayKey);
      if (hasFuture && !has) {
        const next = new Set(prev);
        next.add(todayKey);
        return next;
      }
      if (!hasFuture && has) {
        const next = new Set(prev);
        next.delete(todayKey);
        return next;
      }
      return prev;
    });
  }, [slotsData, selectedDate]);

  const createBookingMutation = useMutation({
    mutationFn: async (data: CustomerInfoFormData) => {
      if (!selectedSlot || !selectedService) throw new Error('No slot or service selected');

      const customerSnapshot = customerMode === 'existing' && selectedCustomer
        ? {
            email: selectedCustomer.email,
            first_name: selectedCustomer.firstName,
            last_name: selectedCustomer.lastName,
            phone: selectedCustomer.phone || undefined,
          }
        : {
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            phone: data.phone || undefined,
          };

      const customerId = customerMode === 'existing' && selectedCustomer
        ? (selectedCustomer.publicId ?? selectedCustomer.id)
        : undefined;

      // Group-class slots come back with a numeric `available_slot_id` and route
      // through the legacy slot-based endpoint. Per-provider availability slots
      // have a synthetic string ID + provider_id and use the new endpoint.
      const isAvailabilityBased = typeof selectedSlot.id === 'string' && (selectedSlot as any).provider_id;

      if (isAvailabilityBased) {
        return adminBookingService.createBookingFromAvailability({
          serviceId: selectedService.id,
          providerId: (selectedSlot as any).provider_id,
          startTime: selectedSlot.start_time || selectedSlot.startTime!,
          customerEmail: customerSnapshot.email,
          customerFirstName: customerSnapshot.first_name,
          customerLastName: customerSnapshot.last_name,
          customerPhone: customerSnapshot.phone,
          notes: data.notes || undefined,
          initialStatus: data.status,
          customerId,
        });
      }

      const bookingData: AdminBookingCreate & { customer_id?: number | string } = {
        available_slot_id: selectedSlot.id as number,
        customer_email: customerSnapshot.email,
        customer_first_name: customerSnapshot.first_name,
        customer_last_name: customerSnapshot.last_name,
        customer_phone: customerSnapshot.phone,
        notes: data.notes || undefined,
        initial_status: data.status,
        customer_id: customerId,
      };

      return adminBookingService.createAdminBooking(bookingData);
    },
    onSuccess: () => {
      toast.success(t('newBooking.toasts.success'));
      setLocation('/admin/bookings');
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.message ||
        'Failed to create booking';
      toast.error(errorMessage);
    },
  });

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    // Reset slot when switching service — group/individual slots differ.
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot: AvailableSlot) => {
    setSelectedSlot(slot);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeekStart =
      direction === 'prev'
        ? addDays(currentWeekStart, -7)
        : addDays(currentWeekStart, 7);
    setCurrentWeekStart(newWeekStart);
    setSelectedDate(newWeekStart);
  };

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i)),
    [currentWeekStart]
  );

  const stepStateAt = (idx: number): StepState =>
    idx < currentStep ? 'done' : idx === currentStep ? 'active' : 'locked';

  const onEditAt = (idx: number) =>
    idx < currentStep ? () => setCurrentStep(idx as WizardStep) : undefined;

  const canProceedFromStep0 = bookingType !== null;
  const canProceedFromStep1 = selectedService !== null;
  const canProceedFromStep2 = selectedSlot !== null;
  const canProceedFromStep3 =
    customerMode === 'existing' ? selectedCustomer !== null : true;

  const goToStep = (next: WizardStep) => setCurrentStep(next);
  const goNext = () => setCurrentStep((s) => Math.min(4, s + 1) as WizardStep);
  const goPrev = () => setCurrentStep((s) => Math.max(0, s - 1) as WizardStep);

  const handleBookingTypeNext = () => {
    if (bookingType === 'recurring') {
      const params = new URLSearchParams({ step: '2' });
      if (presetCustomerId) params.set('customer_id', presetCustomerId);
      setLocation(`/admin/bookings/new/recurring?${params.toString()}`);
      return;
    }
    goNext();
  };

  const handleCustomerStepSubmit = (data: CustomerInfoFormData) => {
    if (customerMode === 'existing') {
      if (!selectedCustomer) return;
    }
    void data;
    goToStep(4);
  };

  const handleCreateBooking = () => {
    if (customerMode === 'existing') {
      // Use the form's status/notes only.
      const v = watch();
      createBookingMutation.mutate(v);
    } else {
      handleSubmit((d) => createBookingMutation.mutate(d))();
    }
  };

  // Summaries for collapsed step cards
  const bookingTypeSummary = (() => {
    if (bookingType === 'one_time') return t('newBooking.bookingType.oneTime');
    if (bookingType === 'recurring') return t('newBooking.bookingType.recurring');
    return undefined;
  })();

  const serviceSummary = (() => {
    if (!selectedService) return undefined;
    const duration = selectedService.duration || selectedService.duration_minutes;
    const parts = [selectedService.name, duration ? `${duration} min` : null].filter(Boolean);
    return parts.join(' · ');
  })();

  const slotSummary = (() => {
    if (!selectedSlot) return undefined;
    const start = parseISO(selectedSlot.startTime || selectedSlot.start_time!);
    const provider =
      selectedSlot.provider?.displayName ||
      (selectedSlot.provider as any)?.name;
    return [format(start, 'EEE d MMM · HH:mm'), provider].filter(Boolean).join(' · ');
  })();

  const customerSummary = (() => {
    if (customerMode === 'existing' && selectedCustomer) {
      return `${selectedCustomer.fullName} · ${selectedCustomer.email}`;
    }
    if (customerMode === 'new') {
      const v = watch();
      const name = [v.first_name, v.last_name].filter(Boolean).join(' ');
      const parts = [name, v.email].filter(Boolean);
      return parts.length ? parts.join(' · ') : undefined;
    }
    return undefined;
  })();

  // Reset new-customer form fields if the user fills them once but then
  // picks an existing customer, to avoid stale review data.
  useEffect(() => {
    if (customerMode === 'existing' && selectedCustomer) {
      // Keep notes/status but clear the four PII fields.
      const v = watch();
      resetForm({
        ...v,
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
      });
    }
  }, [customerMode, selectedCustomer]);

  const dashSoft = <span className="text-soft">—</span>;
  const summaryRows = [
    {
      label: t('newBooking.summary.service', 'Service'),
      value: serviceSummary ?? dashSoft,
    },
    {
      label: t('newBooking.summary.when', 'When'),
      value: slotSummary ?? dashSoft,
    },
    {
      label: t('newBooking.summary.customer', 'Customer'),
      value: customerSummary ?? dashSoft,
    },
  ];
  const totalValue =
    selectedService?.price != null && !selectedService.hide_price
      ? formatCurrency(selectedService.price)
      : undefined;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6" data-testid="booking-wizard">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0">
        <Stepper active={currentStep + 1}>
        {/* Step 1: Booking Type */}
        <StepCard
          step={1}
          title={t('newBooking.stepTitles.step1')}
          state={stepStateAt(0)}
          summary={bookingTypeSummary}
          onEdit={onEditAt(0)}
        >
          <BookingTypeStep
            value={bookingType}
            onChange={setBookingType}
            t={t}
          />
          <StepNav
            onPrev={undefined}
            onNext={handleBookingTypeNext}
            disabledNext={!canProceedFromStep0}
            t={t}
          />
        </StepCard>

        {/* Step 2: Service */}
        <StepCard
          step={2}
          title={t('newBooking.stepTitles.step2')}
          state={stepStateAt(1)}
          summary={serviceSummary}
          onEdit={onEditAt(1)}
        >
          <ServiceStep
            services={activeServices}
            selectedService={selectedService}
            onSelect={handleServiceSelect}
            isLoading={isLoadingServices}
            formatCurrency={formatCurrency}
            t={t}
          />
          <StepNav
            onPrev={goPrev}
            onNext={() => goToStep(2)}
            disabledNext={!canProceedFromStep1}
            t={t}
          />
        </StepCard>

        {/* Step 3: Date & Time */}
        <StepCard
          step={3}
          title={t('newBooking.stepTitles.step3')}
          state={stepStateAt(2)}
          summary={slotSummary}
          onEdit={onEditAt(2)}
        >
          <DateTimeStep
            selectedService={selectedService}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            currentWeekStart={currentWeekStart}
            datesWithSlots={datesWithSlots}
            hasFutureSlots={hasFutureSlots}
            navigateWeek={navigateWeek}
            weekDays={weekDays}
            slots={slotsData}
            selectedSlot={selectedSlot}
            onSelectSlot={handleSlotSelect}
            isLoadingSlots={isLoadingSlots}
            t={t}
          />
          <StepNav
            onPrev={goPrev}
            onNext={goNext}
            disabledNext={!canProceedFromStep2}
            t={t}
          />
        </StepCard>

        {/* Step 4: Customer */}
        <StepCard
          step={4}
          title={t('newBooking.stepTitles.step4')}
          state={stepStateAt(3)}
          summary={customerSummary}
          onEdit={onEditAt(3)}
        >
          <CustomerStep
            customerMode={customerMode}
            setCustomerMode={setCustomerMode}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            debouncedCustomerSearch={debouncedCustomerSearch}
            searchResults={customerSearchResult?.customers ?? []}
            isSearching={isSearchingCustomers}
            requirePhone={requirePhone}
            control={control}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={handleCustomerStepSubmit}
            presetLocked={!!presetCustomerId}
            t={t}
          />
          <StepNav
            onPrev={goPrev}
            onNext={() => {
              if (customerMode === 'existing') {
                if (!selectedCustomer) return;
                goNext();
              } else {
                handleSubmit((d) => {
                  void d;
                  goNext();
                })();
              }
            }}
            disabledNext={!canProceedFromStep3}
            t={t}
          />
        </StepCard>

        {/* Step 5: Review */}
        <StepCard
          step={5}
          title={t('newBooking.stepTitles.step5')}
          state={stepStateAt(4)}
        >
          <ReviewStep
            selectedService={selectedService}
            selectedSlot={selectedSlot}
            customerMode={customerMode}
            selectedCustomer={selectedCustomer}
            formData={formData}
            formatCurrency={formatCurrency}
            t={t}
          />

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={goPrev}
              data-testid="back-button"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('newBooking.buttons.back')}
            </Button>
            <Button
              type="button"
              onClick={handleCreateBooking}
              disabled={createBookingMutation.isPending}
              size="lg"
              data-testid="create-booking-button"
            >
              {createBookingMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('newBooking.buttons.creatingBooking')}
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  {t('newBooking.buttons.createBooking')}
                </>
              )}
            </Button>
          </div>
        </StepCard>
      </Stepper>
        </div>

        <aside className="hidden lg:block">
          <BookingSummaryCard
            sticky
            title={t('newBooking.summary.title', 'Booking summary')}
            rows={summaryRows}
            totalLabel={t('newBooking.summary.total', 'Total')}
            totalValue={totalValue}
            reassurance={null}
          />
        </aside>
      </div>
    </div>
  );
}

// ── Step components ───────────────────────────────────────────────────────

function StepNav({
  onPrev,
  onNext,
  disabledNext,
  t,
}: {
  onPrev?: () => void;
  onNext: () => void;
  disabledNext: boolean;
  t: (k: string) => string;
}) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      <Button
        type="button"
        variant="outline"
        onClick={onPrev}
        disabled={!onPrev}
        data-testid="back-button"
      >
        <ChevronLeft className="h-4 w-4 mr-2" />
        {t('newBooking.buttons.back')}
      </Button>
      <Button
        type="button"
        onClick={onNext}
        disabled={disabledNext}
        data-testid="next-button"
      >
        {t('newBooking.buttons.next')}
        <ChevronRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );
}

function ServiceStep({
  services,
  selectedService,
  onSelect,
  isLoading,
  formatCurrency,
  t,
}: {
  services: Service[];
  selectedService: Service | null;
  onSelect: (svc: Service) => void;
  isLoading: boolean;
  formatCurrency: (amount: number) => string;
  t: (k: string) => string;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-soft">{t('newBooking.services.noActive')}</p>
      </div>
    );
  }

  return (
    <RadioGroup
      value={selectedService ? selectedService.id.toString() : undefined}
      onValueChange={(value) => {
        const svc = services.find((s) => s.id.toString() === value);
        if (svc) onSelect(svc);
      }}
      className="gap-3"
    >
      {services.map((service) => {
        const duration = service.duration || service.duration_minutes;
        const isGroup = isGroupService(service);
        return (
          <ServiceListRow
            key={service.id}
            value={service.id.toString()}
            name={
              <span className="flex items-center gap-2">
                <span>{service.name}</span>
                {isGroup && (
                  <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 border border-violet-200">
                    <Users className="h-3 w-3 mr-1" />
                    {t('newBooking.services.group')}
                  </Badge>
                )}
              </span>
            }
            description={service.description || undefined}
            duration={duration}
            price={!service.hide_price ? formatCurrency(service.price || 0) : undefined}
            className={cn(
              isGroup &&
                'border-violet-200 hover:border-violet-400 data-[state=checked]:border-violet-400 data-[state=checked]:bg-violet-50',
            )}
            data-testid="service-card"
          />
        );
      })}
    </RadioGroup>
  );
}

function DateTimeStep({
  selectedService,
  selectedDate,
  setSelectedDate,
  currentWeekStart,
  datesWithSlots,
  hasFutureSlots,
  navigateWeek,
  weekDays,
  slots,
  selectedSlot,
  onSelectSlot,
  isLoadingSlots,
  t,
}: {
  selectedService: Service | null;
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;
  currentWeekStart: Date;
  datesWithSlots: Set<string>;
  hasFutureSlots: boolean | null;
  navigateWeek: (dir: 'prev' | 'next') => void;
  weekDays: Date[];
  slots: AvailableSlot[];
  selectedSlot: AvailableSlot | null;
  onSelectSlot: (slot: AvailableSlot) => void;
  isLoadingSlots: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const dateLocale = getDateLocale();
  const dateFormats = getDateFormats();
  const isGroup = selectedService ? isGroupService(selectedService) : false;

  const gridSlots: GridTimeSlot[] = useMemo(() => {
    const now = Date.now();
    return slots
      .map((slot) => {
        const startStr = slot.startTime || slot.start_time!;
        const start = parseISO(startStr);
        const providerName =
          slot.provider?.displayName || (slot.provider as any)?.name || undefined;
        const id = String(slot.id);
        const max = slot.maxCapacity ?? slot.max_capacity ?? 1;
        const remaining = slot.spotsRemaining ?? slot.spots_remaining;
        const full = slot.isFull ?? slot.is_full;
        const showCapacity = max > 1;
        return {
          id,
          start,
          sublabel: providerName,
          taken: !!full,
          disabled: !!full,
          footer: showCapacity ? (
            <CapacityBar maxCapacity={max} spotsRemaining={remaining ?? 0} isFull={!!full} t={t} />
          ) : undefined,
        } as GridTimeSlot;
      })
      .filter((s) => s.start.getTime() > now)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [slots, t]);

  if (hasFutureSlots === false) {
    return (
      <Alert>
        <AlertDescription>{t('newBooking.calendar.noFutureSlots')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-col gap-2 mb-3 md:flex-row md:items-center md:justify-between">
          <Label>{t('newBooking.calendar.selectDate')}</Label>
          <div className="flex items-center justify-between md:justify-end gap-1 md:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-w-[44px] min-h-[44px] px-2 md:px-3"
              onClick={() => navigateWeek('prev')}
              aria-label={t('newBooking.calendar.previousWeek')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs md:text-sm font-medium text-soft min-w-[140px] md:min-w-[200px] text-center">
              {format(currentWeekStart, dateFormats.dayMonth, { locale: dateLocale })} - {format(addDays(currentWeekStart, 6), dateFormats.medium, { locale: dateLocale })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-w-[44px] min-h-[44px] px-2 md:px-3"
              onClick={() => navigateWeek('next')}
              aria-label={t('newBooking.calendar.nextWeek')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Mobile: scrollable day cards */}
        <div className="md:hidden overflow-x-auto -mx-4 px-4 snap-x snap-mandatory" data-testid="calendar">
          <div className="flex gap-2 pb-2">
            {weekDays.map((day) => (
              <DayCard
                key={day.toISOString()}
                day={day}
                selectedDate={selectedDate}
                datesWithSlots={datesWithSlots}
                onSelect={setSelectedDate}
                size="mobile"
              />
            ))}
          </div>
        </div>

        {/* Desktop: grid */}
        <div className="hidden md:grid grid-cols-7 gap-2" data-testid="calendar-desktop">
          {weekDays.map((day) => (
            <DayCard
              key={day.toISOString()}
              day={day}
              selectedDate={selectedDate}
              datesWithSlots={datesWithSlots}
              onSelect={setSelectedDate}
              size="desktop"
            />
          ))}
        </div>
      </div>

      {/* Time slots */}
      <div>
        {(isLoadingSlots || gridSlots.length > 0) && (
          <Label className="mb-3">
            {t('newBooking.calendar.availableTimes', {
              date: format(selectedDate, dateFormats.full, { locale: dateLocale }),
            })}
          </Label>
        )}

        {isLoadingSlots ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-[52px] bg-surface-2 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : gridSlots.length === 0 ? (
          <Alert>
            <AlertDescription>{t('newBooking.calendar.noSlots')}</AlertDescription>
          </Alert>
        ) : (
          <TimeSlotGrid
            slots={gridSlots}
            selectedId={selectedSlot ? String(selectedSlot.id) : undefined}
            onSelect={(gs) => {
              const original = slots.find((s) => String(s.id) === gs.id);
              if (original) onSelectSlot(original);
            }}
          />
        )}

        {isGroup && gridSlots.length > 0 && (
          <p className="mt-2 text-body-xs text-soft">
            {t('newBooking.calendar.groupHint')}
          </p>
        )}
      </div>
    </div>
  );
}

function DayCard({
  day,
  selectedDate,
  datesWithSlots,
  onSelect,
  size,
}: {
  day: Date;
  selectedDate: Date;
  datesWithSlots: Set<string>;
  onSelect: (d: Date) => void;
  size: 'mobile' | 'desktop';
}) {
  const dateLocale = getDateLocale();
  const dateStr = format(day, 'yyyy-MM-dd');
  const hasSlots = datesWithSlots.has(dateStr);
  const isSelected = isSameDay(day, selectedDate);
  const isCurrentDay = isToday(day);
  const dayIsPast = isPast(day) && !isCurrentDay;

  const base =
    size === 'mobile'
      ? 'relative min-w-[72px] h-[76px] flex-shrink-0 rounded-lg text-center snap-center'
      : 'relative min-h-[80px] p-4 rounded-lg text-center transition-all';

  return (
    <button
      type="button"
      disabled={dayIsPast}
      onClick={() => !dayIsPast && onSelect(day)}
      className={cn(
        base,
        isSelected
          ? 'bg-brand-500 text-white shadow-md'
          : dayIsPast
          ? 'bg-surface-2 text-soft cursor-not-allowed'
          : isCurrentDay
          ? 'bg-brand-50 border-2 border-brand-300 hover:bg-brand-100'
          : 'bg-white border border-line hover:border-brand-300 hover:shadow-sm',
      )}
      data-testid="calendar-day"
      data-date={dateStr}
    >
      {hasSlots && !dayIsPast && (
        <div className="absolute top-2 right-2">
          <div className={cn('w-3 h-3 rounded-full', isSelected ? 'bg-white' : 'bg-green-500')} />
        </div>
      )}
      {size === 'mobile' ? (
        <div className="flex flex-col items-center justify-center h-full px-2">
          <p className="text-xs font-medium mb-1">{format(day, 'EEE', { locale: dateLocale })}</p>
          <p className="text-2xl font-bold leading-none">{format(day, 'd')}</p>
          <p className="text-xs mt-1 opacity-75">{format(day, 'MMM', { locale: dateLocale })}</p>
        </div>
      ) : (
        <>
          <p className="text-xs font-medium mb-1">{format(day, 'EEE', { locale: dateLocale })}</p>
          <p className="text-2xl font-bold">{format(day, 'd')}</p>
          <p className="text-xs mt-1 opacity-75">{format(day, 'MMM', { locale: dateLocale })}</p>
        </>
      )}
    </button>
  );
}

function CapacityBar({
  maxCapacity,
  spotsRemaining,
  isFull,
  t,
}: {
  maxCapacity: number;
  spotsRemaining: number;
  isFull: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const pct = isFull ? 1 : Math.max(0, spotsRemaining) / Math.max(1, maxCapacity);
  return (
    <div className="space-y-1">
      <div className="w-full h-1.5 bg-surface-2 rounded-pill overflow-hidden">
        <div
          className={cn(
            'h-full transition-all',
            isFull ? 'bg-danger' : pct > 0.6 ? 'bg-success' : pct > 0.3 ? 'bg-warn' : 'bg-danger',
          )}
          style={{ width: isFull ? '100%' : `${pct * 100}%` }}
        />
      </div>
      {isFull ? (
        <Badge variant="destructive" className="text-[11px] px-1.5 py-0.5">
          {t('newBooking.capacity.fullyBooked')}
        </Badge>
      ) : (
        <p className="text-[11px] text-soft">
          {t('newBooking.capacity.spotsLeft', { count: spotsRemaining })}
        </p>
      )}
    </div>
  );
}

function CustomerStep({
  customerMode,
  setCustomerMode,
  selectedCustomer,
  setSelectedCustomer,
  customerSearch,
  setCustomerSearch,
  debouncedCustomerSearch,
  searchResults,
  isSearching,
  requirePhone,
  control,
  errors,
  handleSubmit,
  onSubmit,
  presetLocked,
  t,
}: {
  customerMode: CustomerMode;
  setCustomerMode: (m: CustomerMode) => void;
  selectedCustomer: Customer | null;
  setSelectedCustomer: (c: Customer | null) => void;
  customerSearch: string;
  setCustomerSearch: (q: string) => void;
  debouncedCustomerSearch: string;
  searchResults: Customer[];
  isSearching: boolean;
  requirePhone: boolean;
  control: any;
  errors: any;
  handleSubmit: any;
  onSubmit: (data: CustomerInfoFormData) => void;
  presetLocked: boolean;
  t: (k: string) => string;
}) {
  const isActiveSearch = debouncedCustomerSearch.trim().length > 0;
  const visibleResults = isActiveSearch ? searchResults : searchResults.slice(0, 3);
  return (
    <Tabs
      value={customerMode}
      onValueChange={(v) => {
        if (presetLocked) return;
        setCustomerMode(v as CustomerMode);
      }}
    >
      <TabsList className="w-full grid grid-cols-2">
        <TabsTrigger value="existing" data-testid="tab-existing-customer">
          <Search className="h-4 w-4 mr-2" />
          {t('newBooking.customer.existing')}
        </TabsTrigger>
        <TabsTrigger value="new" data-testid="tab-new-customer" disabled={presetLocked}>
          <User className="h-4 w-4 mr-2" />
          {t('newBooking.customer.new')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="existing" className="space-y-4 mt-4">
        {selectedCustomer ? (
          <SelectedCustomerCard
            customer={selectedCustomer}
            onClear={presetLocked ? undefined : () => setSelectedCustomer(null)}
            t={t}
          />
        ) : (
          <>
            <SearchBar
              placeholder={t('newBooking.customer.searchPlaceholder')}
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              data-testid="customer-search-input"
            />

            {isSearching && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-soft" />
              </div>
            )}

            {!isSearching && !isActiveSearch && searchResults.length > 0 && (
              <p className="text-body-xs text-soft">
                {t('newBooking.customer.recentHint')}
              </p>
            )}

            {!isSearching && isActiveSearch && searchResults.length === 0 && (
              <Alert>
                <AlertDescription>
                  {t('newBooking.customer.noResults')}
                </AlertDescription>
              </Alert>
            )}

            {!isSearching && visibleResults.length > 0 && (
              <div className="rounded-xl border border-line divide-y divide-line">
                {visibleResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedCustomer(c)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-2 transition-colors"
                    data-testid="customer-result"
                  >
                    <div className="h-9 w-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center text-body-sm font-semibold">
                      {c.fullName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-body font-semibold text-ink truncate">
                        {c.fullName}
                      </div>
                      <div className="text-body-sm text-soft truncate">
                        {c.email}
                        {c.phone && <> · {c.phone}</>}
                      </div>
                    </div>
                    <Badge tone="neutral" className="shrink-0">
                      {c.totalVisits}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <BookingExtras
          control={control}
          errors={errors}
          showRequiredPhone={false}
        />
      </TabsContent>

      <TabsContent value="new" className="mt-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('newBooking.form.firstName')} required icon={<User className="h-4 w-4" />} error={errors.first_name?.message}>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={cn(errors.first_name && 'border-red-500')} />
                )}
              />
            </FormField>
            <FormField label={t('newBooking.form.lastName')} required icon={<User className="h-4 w-4" />} error={errors.last_name?.message}>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <Input {...field} className={cn(errors.last_name && 'border-red-500')} />
                )}
              />
            </FormField>
            <FormField label={t('newBooking.form.email')} required icon={<Mail className="h-4 w-4" />} error={errors.email?.message}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="email"
                    placeholder={t('newBooking.form.emailPlaceholder')}
                    className={cn(errors.email && 'border-red-500')}
                  />
                )}
              />
            </FormField>
            <FormField
              label={requirePhone ? t('newBooking.form.phone') : t('newBooking.form.phoneOptional')}
              required={requirePhone}
              icon={<PhoneIcon className="h-4 w-4" />}
              error={errors.phone?.message}
            >
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="+1 (555) 000-0000"
                  />
                )}
              />
            </FormField>
          </div>

          <BookingExtras control={control} errors={errors} showRequiredPhone />
        </form>
      </TabsContent>
    </Tabs>
  );
}

function SelectedCustomerCard({
  customer,
  onClear,
  t,
}: {
  customer: Customer;
  onClear?: () => void;
  t: (k: string) => string;
}) {
  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 flex items-start gap-3" data-testid="selected-customer">
      <div className="h-10 w-10 rounded-full bg-brand-500 text-white flex items-center justify-center font-semibold">
        {customer.fullName.slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-body font-semibold text-ink">{customer.fullName}</div>
        <div className="text-body-sm text-soft">
          {customer.email}
          {customer.phone && <> · {customer.phone}</>}
        </div>
        <div className="text-body-xs text-soft mt-1">
          {t('newBooking.customer.totalVisits')}: {customer.totalVisits}
        </div>
      </div>
      {onClear && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          aria-label={t('newBooking.customer.clear')}
          data-testid="clear-selected-customer"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function BookingExtras({
  control,
  errors,
  showRequiredPhone,
}: {
  control: any;
  errors: any;
  showRequiredPhone: boolean;
}) {
  void errors;
  void showRequiredPhone;
  const { t } = useTranslation('bookings');
  return (
    <div className="space-y-4">
      <FormField
        label={t('newBooking.form.initialStatus')}
        required
        icon={<FileText className="h-4 w-4" />}
        helper={t('newBooking.form.statusDescription')}
      >
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed" data-testid="status-option-confirmed">
                  {t('newBooking.form.statusConfirmed')}
                </SelectItem>
                <SelectItem value="pending" data-testid="status-option-pending">
                  {t('newBooking.form.statusPending')}
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </FormField>

      <FormField label={t('newBooking.form.notesOptional')} icon={<FileText className="h-4 w-4" />}>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder={t('newBooking.form.notesPlaceholder')}
              rows={3}
            />
          )}
        />
      </FormField>
    </div>
  );
}

function FormField({
  label,
  required,
  icon,
  error,
  helper,
  children,
}: {
  label: React.ReactNode;
  required?: boolean;
  icon?: React.ReactNode;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {icon}
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {!error && helper && <p className="text-sm text-soft">{helper}</p>}
    </div>
  );
}

function ReviewStep({
  selectedService,
  selectedSlot,
  customerMode,
  selectedCustomer,
  formData,
  formatCurrency,
  t,
}: {
  selectedService: Service | null;
  selectedSlot: AvailableSlot | null;
  customerMode: CustomerMode;
  selectedCustomer: Customer | null;
  formData: CustomerInfoFormData;
  formatCurrency: (amount: number) => string;
  t: (k: string) => string;
}) {
  const dateLocale = getDateLocale();
  const dateFormats = getDateFormats();
  const customerName =
    customerMode === 'existing' && selectedCustomer
      ? selectedCustomer.fullName
      : `${formData.first_name} ${formData.last_name}`.trim();
  const customerEmail =
    customerMode === 'existing' && selectedCustomer
      ? selectedCustomer.email
      : formData.email;
  const customerPhone =
    customerMode === 'existing' && selectedCustomer
      ? selectedCustomer.phone
      : formData.phone;

  return (
    <div className="space-y-6" data-testid="review-summary">
      <div className="rounded-2xl border border-line bg-surface p-5 space-y-5">
        <Section title={t('newBooking.review.serviceDetails')}>
          <Row label={t('newBooking.review.service')} value={selectedService?.name} />
          {selectedService?.description && (
            <Row label={t('newBooking.review.description')} value={selectedService.description} />
          )}
          <Row
            label={t('newBooking.review.duration')}
            value={`${selectedService?.duration || selectedService?.duration_minutes} ${t('newBooking.review.minutes')}`}
          />
          <Row
            label={t('newBooking.review.price')}
            value={formatCurrency(selectedService?.price || 0)}
          />
        </Section>

        <Divider />

        <Section title={t('newBooking.review.appointmentDetails')}>
          <Row
            icon={<Calendar className="h-4 w-4 text-soft" />}
            label={t('newBooking.review.date')}
            value={
              selectedSlot &&
              format(parseISO(selectedSlot.startTime || selectedSlot.start_time!), dateFormats.fullWithWeekday, { locale: dateLocale })
            }
          />
          <Row
            icon={<Clock className="h-4 w-4 text-soft" />}
            label={t('newBooking.review.time')}
            value={
              selectedSlot &&
              format(parseISO(selectedSlot.startTime || selectedSlot.start_time!), 'HH:mm')
            }
          />
          <Row
            icon={<User className="h-4 w-4 text-soft" />}
            label={t('newBooking.review.provider')}
            value={
              selectedSlot?.provider?.displayName ||
              (selectedSlot?.provider as any)?.name ||
              t('newBooking.review.provider')
            }
          />
        </Section>

        <Divider />

        <Section title={t('newBooking.review.customerDetails')}>
          <Row label={t('newBooking.review.name')} value={customerName} />
          <Row label={t('newBooking.review.email')} value={customerEmail} />
          {customerPhone && <Row label={t('newBooking.review.phone')} value={customerPhone} />}
          <Row
            label={t('newBooking.review.status')}
            value={
              <Badge
                className={cn(
                  formData.status === 'confirmed' ? 'bg-green-500' : 'bg-yellow-500',
                )}
              >
                {formData.status === 'confirmed'
                  ? t('newBooking.form.statusConfirmed')
                  : t('newBooking.form.statusPending')}
              </Badge>
            }
          />
          {formData.notes && <Row label={t('newBooking.review.notes')} value={formData.notes} />}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-h3 text-ink">{title}</h3>
      <div className="space-y-1.5 text-body-sm">{children}</div>
    </div>
  );
}

function Divider() {
  return <div className="border-t border-line" />;
}

function Row({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: React.ReactNode;
  value?: React.ReactNode;
}) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="flex items-center gap-1.5 text-soft">
        {icon}
        {label}
      </span>
      <span className="font-medium text-ink text-right max-w-[60%]">{value}</span>
    </div>
  );
}
